// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useOperatorParty } from '@hooks/queries/utility-operator/useOperatorParty';
import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import FormDialog from '../../../components/Dialog/FormDialog';
import useCreateOperatorConfiguration from '../../../hooks/mutations/registry/configuration/useCreateOperatorConfiguration';
import {
  PartyCredentialRequirementElement,
  toPartyCredentialRequirements,
} from '../../../modules/Credential/Common';
import RequiredCredentialsInput from '../../../modules/Credential/RequiredCredentialsInput';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const CreateOperatorConfigurationDialog = ({ open, onClose }: DialogProps) => {
  const [providerCredentialRequirements, setProviderCredentialRequirements] = useState<
    PartyCredentialRequirementElement[]
  >([]);

  const { data: operator } = useOperatorParty();
  const createOperatorConfiguration = useCreateOperatorConfiguration();

  const resetState = useCallback(() => {
    setProviderCredentialRequirements([]);
  }, [setProviderCredentialRequirements]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!operator) {
      return;
    }

    await createOperatorConfiguration({
      argument: {
        operator,
        providerRequirements: toPartyCredentialRequirements(providerCredentialRequirements),
      },
    });
    handleClose();
  }, [operator, createOperatorConfiguration, providerCredentialRequirements, handleClose]);

  return (
    <FormDialog
      open={open}
      title="Create Operator Configuration"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Create"
      isSubmitDisabled={false}
    >
      <Box mb={3}>
        <RequiredCredentialsInput
          list={providerCredentialRequirements}
          setList={setProviderCredentialRequirements}
          title="Required Credentials for Providers"
        />
      </Box>
    </FormDialog>
  );
};
